// The same Ed25519 implementation @solana/web3.js verifies with, so a signature
// this accepts is one the chain accepts.
import { ed25519 } from '@noble/curves/ed25519';
import bs58 from 'bs58';
/**
 * The compiled message a signature actually covers. Comparing it before and
 * after a wallet call proves the wallet signed what we asked it to sign, rather
 * than something it rewrote.
 */
export function transactionMessageBytes(transaction) {
    if (typeof (transaction === null || transaction === void 0 ? void 0 : transaction.serializeMessage) === 'function') {
        return new Uint8Array(transaction.serializeMessage());
    }
    const message = transaction === null || transaction === void 0 ? void 0 : transaction.message;
    if (typeof (message === null || message === void 0 ? void 0 : message.serialize) === 'function')
        return message.serialize();
    // Neither shape. Returning "no bytes" would make every comparison against it
    // trivially equal, which is the one answer this must never give.
    throw new Error('Cannot read the message of this transaction, so what a wallet signs for it cannot be checked. ' +
        'Pass a Solana Transaction or VersionedTransaction.');
}
/**
 * Did `address` actually sign this transaction?
 *
 * Observing which account a wallet REPORTS after a call does not answer that: a
 * wallet holding several authorized accounts can keep reporting the first while
 * signing with another, and Solana Mobile discards the account a Wallet Standard
 * request names.
 *
 * Nor does a filled signature SLOT answer it. Slots are positional - the wallet
 * writes whatever it produced into the position of the required signer - so a
 * wallet that signed with another key puts THAT key's signature in this one's
 * slot, and a slot check waves it through. Only verifying the signature against
 * the message and this exact public key answers the question, which is what the
 * chain itself will do later; doing it here turns a transaction that would be
 * rejected on arrival into a refusal with a reason.
 */
export function transactionSignedBy(transaction, address) {
    var _a, _b, _c;
    const message = transactionMessageBytes(transaction);
    let key;
    try {
        key = bs58.decode(address);
    }
    catch (_d) {
        return false;
    }
    if (key.length !== 32)
        return false;
    const verify = (signature) => {
        if (!signature || signature.length !== 64)
            return false;
        try {
            return ed25519.verify(signature, message, key);
        }
        catch (_a) {
            // Malformed point/scalar: not a signature by this key.
            return false;
        }
    };
    if (typeof (transaction === null || transaction === void 0 ? void 0 : transaction.serializeMessage) === 'function') {
        const legacy = (_a = transaction.signatures) !== null && _a !== void 0 ? _a : [];
        return legacy.some((s) => { var _a, _b; return ((_b = (_a = s.publicKey) === null || _a === void 0 ? void 0 : _a.toBase58) === null || _b === void 0 ? void 0 : _b.call(_a)) === address && verify(s.signature); });
    }
    const versioned = transaction;
    if (!((_b = versioned === null || versioned === void 0 ? void 0 : versioned.message) === null || _b === void 0 ? void 0 : _b.staticAccountKeys))
        return false;
    const signers = versioned.message.staticAccountKeys.slice(0, versioned.message.header.numRequiredSignatures);
    const index = signers.findIndex((k) => k.toBase58() === address);
    if (index < 0)
        return false;
    return verify((_c = versioned.signatures) === null || _c === void 0 ? void 0 : _c[index]);
}
// ---------------------------------------------------------------------------
// LF-003 part 2: wallets that GUARD a transaction rather than rewrite it.
//
// Phantom injects Lighthouse (L2TExMFKdjpN9kozasaurPirfHy9P8sbXoAN1qA3S95)
// assertion instructions into mainnet transactions so the transaction aborts if
// balances move in ways its simulation did not predict. Doing that adds
// instructions AND a program key, and web3.js then RECOMPILES the message, which
// renumbers every account index in the transaction.
//
// Measured on a real mainnet failure (LOCKFUN, 2026-08-25): 696 -> 890 bytes,
// 13 -> 14 static keys, 4 -> 11 instructions, and the Bounded instruction's own
// account indexes went [0,7,5,9,7,0,6,1,2,4,3,12,8,11] ->
// [0,7,6,13,7,0,1,2,3,5,4,10,12,11]. The blockhash, the fee payer, the
// instruction DATA and - decisively - the RESOLVED account list of that
// instruction were all unchanged.
//
// So byte equality cannot be satisfied by any wallet that does this, while the
// property that actually matters survives intact. These helpers check that
// property instead: every instruction we sent still means exactly what it meant.
// ---------------------------------------------------------------------------
const COMPUTE_BUDGET_PROGRAM = 'ComputeBudget111111111111111111111111111111';
/**
 * A STABLE identity for the account an index refers to, valid across a recompile
 * that renumbers the table.
 *
 * Indexes below `staticAccountKeys.length` name a key directly. Above it they
 * address the lookup-table space - writable slots of every lookup in order, then
 * the readonly slots - which cannot be resolved to a pubkey offline, so those
 * are identified by `<table>#w<slot>` / `<table>#r<slot>` instead. That is
 * equally stable: the same table and slot is the same account.
 */
function accountIdentityAt(message, index) {
    const staticKeys = message.staticAccountKeys;
    if (index < 0)
        return null;
    if (index < staticKeys.length)
        return staticKeys[index].toBase58();
    let offset = index - staticKeys.length;
    for (const lookup of message.addressTableLookups) {
        if (offset < lookup.writableIndexes.length)
            return `${lookup.accountKey.toBase58()}#w${lookup.writableIndexes[offset]}`;
        offset -= lookup.writableIndexes.length;
    }
    for (const lookup of message.addressTableLookups) {
        if (offset < lookup.readonlyIndexes.length)
            return `${lookup.accountKey.toBase58()}#r${lookup.readonlyIndexes[offset]}`;
        offset -= lookup.readonlyIndexes.length;
    }
    return null;
}
function toHex(bytes) {
    let out = '';
    for (const byte of bytes)
        out += byte.toString(16).padStart(2, '0');
    return out;
}
/** Program id + data + the accounts it acts on, independent of index numbering. */
function instructionIdentity(message, index) {
    const instruction = message.compiledInstructions[index];
    const program = accountIdentityAt(message, instruction.programIdIndex);
    if (program === null)
        return null;
    const accounts = [];
    for (const accountIndex of instruction.accountKeyIndexes) {
        const identity = accountIdentityAt(message, accountIndex);
        // An index that resolves to nothing makes the instruction unreadable, so
        // it cannot be shown to still mean what it meant. Fail closed.
        if (identity === null)
            return null;
        // The ROLE is part of what an account means to a program - a document
        // account demoted to readonly is a different instruction, and the real
        // guard preserves every role (measured on the mainnet capture), so
        // binding them costs nothing and turns an on-chain failure into a
        // refusal that has not yet spent a fee.
        const signer = message.isAccountSigner(accountIndex) ? 's' : '-';
        const writable = message.isAccountWritable(accountIndex) ? 'w' : '-';
        accounts.push(`${identity}${signer}${writable}`);
    }
    return `${program}|${accounts.join(',')}|${toHex(instruction.data)}`;
}
function isComputeBudget(message, index) {
    return accountIdentityAt(message, message.compiledInstructions[index].programIdIndex) === COMPUTE_BUDGET_PROGRAM;
}
/**
 * Does the message a wallet returned still carry, intact, every instruction we
 * asked it to sign?
 *
 * ACCEPTS a wallet that added guard instructions, renumbered the account table,
 * or retuned the compute budget. REFUSES anything that touches what our
 * instructions actually do.
 *
 * ComputeBudget instructions are the one exception on OUR side: they carry no
 * accounts and cannot move value, they only price and bound the transaction, and
 * a guarding wallet has to raise the unit limit to pay for the guard it just
 * added (measured: 155670 -> 162075). Every other instruction we sent must still
 * be present, in the same relative order, with the same program, the same data
 * bytes, and the same resolved accounts.
 *
 * Returns null when the returned message is acceptable, or the reason it is not.
 */
export function describeInstructionDivergence(sent, returned) {
    if (sent.recentBlockhash !== returned.recentBlockhash) {
        return `it replaced the blockhash (asked to sign against ${sent.recentBlockhash}, signed ` +
            `against ${returned.recentBlockhash})`;
    }
    if (sent.header.numRequiredSignatures !== returned.header.numRequiredSignatures) {
        return 'it changed how many signatures the transaction requires';
    }
    for (let i = 0; i < sent.header.numRequiredSignatures; i++) {
        if (accountIdentityAt(sent, i) !== accountIdentityAt(returned, i)) {
            return 'it changed which accounts must sign the transaction';
        }
    }
    // Walk our instructions in order and find each one in the returned message,
    // only ever moving forwards, never reusing a match - so a wallet may
    // interleave its own, but cannot reorder or drop ours.
    let cursor = 0;
    const matched = new Set();
    const ourPrograms = new Set();
    for (let i = 0; i < sent.compiledInstructions.length; i++) {
        if (isComputeBudget(sent, i))
            continue;
        const program = accountIdentityAt(sent, sent.compiledInstructions[i].programIdIndex);
        if (program === null)
            return 'the transaction it was asked to sign could not be read back';
        ourPrograms.add(program);
        const wanted = instructionIdentity(sent, i);
        if (wanted === null)
            return 'the transaction it was asked to sign could not be read back';
        let found = -1;
        for (let j = cursor; j < returned.compiledInstructions.length; j++) {
            if (instructionIdentity(returned, j) === wanted) {
                found = j;
                break;
            }
        }
        if (found < 0)
            return 'one of the instructions it was asked to sign is missing or altered';
        matched.add(found);
        cursor = found + 1;
    }
    // Surviving is not enough: the wallet must not have added MORE calls to a
    // program this transaction already invokes. A second `set_documents` - the
    // same one duplicated, or a different one appended - executes a second write
    // for a single `set()`, and field operations like increment are not
    // idempotent. The pre-sign validator already demands exactly one Bounded
    // instruction; this is what keeps that true of the bytes that get broadcast.
    // A guard program the transaction never used (Lighthouse) is untouched by
    // this, which is the whole point.
    for (let j = 0; j < returned.compiledInstructions.length; j++) {
        if (matched.has(j))
            continue;
        const program = accountIdentityAt(returned, returned.compiledInstructions[j].programIdIndex);
        if (program !== null && ourPrograms.has(program)) {
            return 'it added another call to a program this transaction already uses';
        }
    }
    return null;
}
